import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { tss } from 'tss-react/mui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { lighten, useTheme } from '@mui/material/styles';
import { SegmentTag } from 'types/global';
import { CovaloLogo } from '../InCosmeticsHeader/CovaloLogo';
import { InCosmeticsLogo } from '../InCosmeticsHeader/InCosmeticsLogo';
import Close from '@mui/icons-material/Close';
import { Trans } from 'react-i18next';

interface WhyRegisterModalProps {
  show: boolean;
  setModal: (a: boolean) => void;
  segment: SegmentTag;
}
export const WhyRegisterModal = ({
  show,
  setModal,
  segment
}: WhyRegisterModalProps) => {
  const auth = useAuth();
  const theme = useTheme();
  const { classes } = useStyles({ segment: segment });
  const { isMobile } = useBreakpoints();
  return (
    <Dialog open={show} onClose={() => setModal(false)} fullWidth maxWidth="lg">
      <div className={classes.root}>
        {isMobile && (
          <IconButton
            onClick={() => setModal(false)}
            sx={{
              justifySelf: 'flex-end',
              position: 'absolute',
              color: segment === SegmentTag.PERSONALCARE ? 'white' : 'default'
            }}
          >
            <Close />
          </IconButton>
        )}
        <div className={classes.imageSection}>
          {segment === SegmentTag.PERSONALCARE ? (
            <div>
              <InCosmeticsLogo /> x <CovaloLogo />
            </div>
          ) : (
            <CovaloLogo fill={theme.palette.primary.main} />
          )}
          <Typography
            sx={{
              color: segment === SegmentTag.PERSONALCARE ? 'white' : 'primary',
              typography: { xs: 'body1', sm: 'h3' }
            }}
          >
            Sign up now to view more Information!
          </Typography>
        </div>
        <div className={classes.tableWrapper}>
          <DialogContent
            sx={{
              scrollBehavior: 'smooth',
              height: 'fit-content',
              overflowY: 'auto',
              px: 0
            }}
          >
            <table>
              <thead>
                <tr style={{ marginBottom: 4 }}>
                  <th></th>
                  <Typography
                    variant="body2"
                    component="th"
                    sx={{
                      textAlign: 'center'
                    }}
                  >
                    Unregistered
                  </Typography>
                  <Typography
                    variant="body2"
                    component="th"
                    sx={{
                      textAlign: 'center'
                    }}
                  >
                    Free account
                  </Typography>
                </tr>
              </thead>
              <tbody className={classes.tableBody}>
                {registerReasons.map(
                  (reason: registerReasonsProps, key: number) => {
                    return (
                      <tr key={key}>
                        <td className={classes.featureTextCell}>
                          <div>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: '500',
                                padding: 0
                              }}
                            >
                              {reason.title}
                            </Typography>
                            {reason.description && (
                              <Typography variant="caption" sx={{ padding: 0 }}>
                                {reason.description}
                              </Typography>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          {reason.unregisteredAccess && (
                            <CheckOutlinedIcon
                              color="primary"
                              fontSize="small"
                              sx={theme => ({
                                background: lighten(
                                  theme.palette.primary.main,
                                  0.5
                                ),
                                borderRadius: '50%',
                                padding: '2px'
                              })}
                            />
                          )}
                        </td>
                        <td
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          {reason.registeredAccess && (
                            <CheckOutlinedIcon
                              color="primary"
                              fontSize="small"
                              sx={theme => ({
                                background: lighten(
                                  theme.palette.primary.main,
                                  0.5
                                ),
                                padding: '2px',
                                borderRadius: '50%'
                              })}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </DialogContent>
          <DialogActions
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1000,
              backgroundColor:
                segment === SegmentTag.PERSONALCARE
                  ? lighten(theme.palette.primary.main, 0.9)
                  : '#f6f5f5'
            }}
          >
            <Button variant="contained" component={Link} to="/register">
              <Trans i18nKey="auth.signup" context="promote" />
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                auth.signinRedirect({
                  redirect_uri:
                    window.location.origin + window.location.pathname
                })
              }
            >
              <Trans i18nKey="auth.login" />
            </Button>
          </DialogActions>
        </div>
      </div>
    </Dialog>
  );
};

interface registerReasonsProps {
  title: string;
  description?: string;
  unregisteredAccess: boolean;
  registeredAccess: boolean;
}
const registerReasons: registerReasonsProps[] = [
  {
    title: 'Access to company information',
    unregisteredAccess: true,
    registeredAccess: true
  },
  {
    title: 'Basic technical information',
    description: 'INCI name, IUPAC name, CAS no., EC no., description',
    unregisteredAccess: true,
    registeredAccess: true
  },
  {
    title: 'Advanced technical information',
    description:
      'Performance and sustainability claims, functions, applications, usage level, chemical group, physical and chemical properties, organoleptic properties, origin, formulation table and procedure',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'Certifications',
    description:
      'AllergyCertified, ChemFORWARD SAFER, China approved, COSMOS, Halal, ISO 16128, Leaping Bunny, NATRUE, RSPO, USDA Certified BioBased, Vegan Society, Vegetarian Society Approved',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'Commercial information',
    description: 'Regional availability, MOQ, launch date',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'View documents',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'Compare up to 4 products side by side',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'Bookmark your favorite products',
    unregisteredAccess: false,
    registeredAccess: true
  },
  {
    title: 'Make requests for documents, quotes and samples',
    unregisteredAccess: false,
    registeredAccess: true
  }
];
const useStyles = tss
  .withParams<{ segment: SegmentTag }>()
  .create(({ theme, segment }) => ({
    root: {
      display: 'grid',
      gridTemplateColumns: '3fr 12fr',
      backgroundColor:
        segment === SegmentTag.PERSONALCARE
          ? lighten(theme.palette.primary.main, 0.9)
          : '#f6f5f5',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr'
      }
    },

    imageSection: {
      backgroundImage: `url("${
        segment === SegmentTag.PERSONALCARE
          ? 'https://static.covalo.com/rx/abstractBubbles.webp'
          : 'https://dev-static.covalo.com/homepage/hex-mobile.webp'
      }")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      color: 'white',
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        padding: '70px 0'
      }
    },
    tableWrapper: {
      padding: '1em 0',
      width: 'calc(100% - 2em)',
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      },
      '& table': {
        borderSpacing: '10px 10px',
        display: 'block'
      },
      '& tbody': {
        display: 'block'
      },
      '& .th-small': {
        fontSize: '12px',
        fontWeight: 'bolder'
      },
      '& tr': {
        display: 'grid',
        gridTemplateColumns: '4fr 1fr 1fr',
        boxSizing: 'border-box',
        '&:nth-of-type(2n+1)': {
          backgroundColor: 'transparent'
        },
        '&:nth-of-type(2n)': {
          '& td': {
            backgroundColor: lighten(theme.palette.primary.main, 0.7)
          }
        }
      },
      '& thead': {
        display: 'block',
        backgroundColor: 'transparent !important',
        borderBottom: 'none'
      }
    },
    featureTextCell: {
      position: 'relative',
      zIndex: '-2',
      paddingLeft: '1em',
      paddingTop: '1em',
      paddingBottom: '1em'
    },
    tableBody: {
      position: 'relative',
      zIndex: '10',
      '&:before': {
        position: 'absolute',
        top: '0',
        left: '66.666%',
        right: '0',
        height: '100%',
        borderRadius: '5px',
        padding: 8,
        gap: 16,
        background: 'white',
        boxShadow:
          '0 6px 12px -2px rgba(50, 50, 93, 0.25), 0 3px 7px -3px rgba(0, 0, 0, 0.3)',
        zIndex: '-1',
        content: '""'
      },
      '& .tableCell-center': {
        textAlign: 'center'
      }
    }
  }));
